import { useEffect, useState } from 'react';
import { Card, IndexTable, Text, TextField, Select, InlineStack, BlockStack, Banner, Box, Badge } from '@shopify/polaris';
import { api, formatEurCents, formatDate, CATEGORY_LABELS } from './shared.js';

export default function PriceBookTab() {
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [data, setData] = useState({ rows: [], total: 0, categories: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ q, category });
        setData(await api(`/price-book?${params}`, { signal: controller.signal }));
        setError('');
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [q, category]);

  const categoryOptions = [{ label: 'All categories', value: '' }, ...data.categories.map((c) => ({ label: CATEGORY_LABELS[c] || c, value: c }))];

  return (
    <BlockStack gap="400">
      <Text as="p" tone="subdued">
        Every component price Elektro Tech charged in the quotes and post-calculations the model was trained on, newest price first. Use it to check a single component or a labour rate.
      </Text>
      <Card padding="0">
        <Box padding="300">
          <InlineStack gap="300" wrap={false}>
            <Box minWidth="320px">
              <TextField label="Search" labelHidden value={q} onChange={setQ} placeholder="e.g. ATV320 1.5kW, CPU 1513, VX 800" clearButton onClearButtonClick={() => setQ('')} autoComplete="off" />
            </Box>
            <Box minWidth="220px">
              <Select label="Category" labelHidden options={categoryOptions} value={category} onChange={setCategory} />
            </Box>
            <Box paddingBlockStart="150">
              <Text as="span" tone="subdued">
                {data.total} component{data.total === 1 ? '' : 's'}
                {data.total > data.rows.length ? ` (first ${data.rows.length})` : ''}
              </Text>
            </Box>
          </InlineStack>
        </Box>
        {error && <Banner tone="critical">{error}</Banner>}
        <IndexTable
          resourceName={{ singular: 'component', plural: 'components' }}
          itemCount={data.rows.length}
          loading={loading}
          selectable={false}
          emptyState={
            <Box padding="600">
              <Text as="p" alignment="center" tone="subdued">
                No prices yet: the price book fills when a super admin trains the model.
              </Text>
            </Box>
          }
          headings={[
            { title: 'Component' },
            { title: 'Category' },
            { title: 'Last price', alignment: 'end' },
            { title: 'Date' },
            { title: 'Range', alignment: 'end' },
            { title: 'Seen', alignment: 'end' }
          ]}
        >
          {data.rows.map((row, index) => (
            <IndexTable.Row id={`${row.category}-${index}`} key={`${row.category}-${row.description}-${index}`} position={index}>
              <IndexTable.Cell>
                <div style={{ maxWidth: 520, whiteSpace: 'normal' }}>
                  <Text as="span">{row.description}</Text>
                </div>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Badge>{CATEGORY_LABELS[row.category] || row.category}</Badge>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text as="span" alignment="end" numeric fontWeight="semibold">
                  {formatEurCents(row.lastPrice)}
                  {row.unit ? ` / ${row.unit}` : ''}
                </Text>
              </IndexTable.Cell>
              <IndexTable.Cell>{formatDate(row.lastDate)}</IndexTable.Cell>
              <IndexTable.Cell>
                <Text as="span" alignment="end" numeric tone="subdued">
                  {row.minPrice === row.maxPrice ? '—' : `${formatEurCents(row.minPrice)} – ${formatEurCents(row.maxPrice)}`}
                </Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text as="span" alignment="end" numeric>
                  {row.count}×
                </Text>
              </IndexTable.Cell>
            </IndexTable.Row>
          ))}
        </IndexTable>
      </Card>
    </BlockStack>
  );
}
